import { UniButton } from '@/design-system/components/buttons/UniButton';
import { UniCheckbox } from '@/design-system/components/inputs/uniCheckbox';
import { H5, H6, TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography/UniTypography';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import CheckCircleSvg from '../../../../assets/svg/Check Circle Outline.svg';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { useNavigatorPage } from '../../hooks/useNavigatorPage.hook';
import ExchangeImageBackground from '../background/exchange-image-background';
import GraphicComponent from './Graphic.component';

export interface ExchangeIntroductionProps {}

export const ExchangeIntroductionMobile: React.FC<ExchangeIntroductionProps> = () => {
  const [selected, setSelected] = useState<boolean>(false);
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const store = useStore();
  const { t } = useTranslation('exchange-sub-account');
  const navigator = useNavigatorPage();
  const onSelectChange = (value: boolean) => {
    setSelected(value);
  };

  const onBtnOpenAccountClick = () => {
    if (!!selected) {
      setSelected(false);
      return navigator.pushScreen(RootStackRoutes.ExchangeSubAccount, ExchangeSubAccountStackRoutes.SelectExchange);
    }
  };

  return (
    <View sx={{ ...sxStylesMobile.container, ...{ width, height } }}>
      <ExchangeImageBackground />
      {Platform.OS !== 'web' && (
        <View sx={{ ...sxStylesMobile.header, ...{ pt: insets.top + 20 } }}>
          <H6 semiBold sx={sxStylesMobile.textHeader}>
            {t('exchange-account::overview.title--exchange-account')}
          </H6>
        </View>
      )}
      <ScrollView sx={{ mb: 10 }} contentContainerStyle={{ flexGrow: 1 }}>
        <View sx={sxStylesMobile.mainView}>
          <View sx={sxStylesMobile.graphicView}>
            <GraphicComponent />
          </View>
          <View sx={sxStylesMobile.infoView}>
            <View sx={sxStylesMobile.body}>
              <View sx={sxStylesMobile.columnView}>
                <View sx={sxStylesMobile.titleView}>
                  <H5 bold sx={sxStylesMobile.titleText}>
                    {t('exchange-account::landing-page.text--discover-possibilities')}
                  </H5>
                </View>
                <View sx={sxStylesMobile.introTextView}>
                  <TextTitle semiBold sx={sxStylesMobile.introText}>
                    {t('exchange-account::landing-page.text--get-connected')}
                  </TextTitle>
                </View>
                <View sx={sxStylesMobile.listTextView}>
                  <View sx={sxStylesMobile.listTextItem}>
                    <TextBody sx={sxStylesMobile.whyTextItem}>{t('exchange-account::landing-page.text--why-utgl')}</TextBody>
                  </View>
                  <View sx={sxStylesMobile.listTextItem}>
                    <View sx={sxStylesMobile.checkView}>
                      <CheckCircleSvg width={20} />
                    </View>
                    <TextCaption semiBold sx={sxStylesMobile.textItem}>
                      {t('exchange-account::landing-page.text--lower-trading-fees')}
                    </TextCaption>
                  </View>
                  <View sx={sxStylesMobile.listTextItem}>
                    <View sx={sxStylesMobile.checkView}>
                      <CheckCircleSvg width={20} />
                    </View>
                    <TextCaption semiBold sx={sxStylesMobile.textItem}>
                      {t('exchange-account::landing-page.text--access-and-enables')}
                    </TextCaption>
                  </View>
                  <View sx={sxStylesMobile.listTextItem}>
                    <View sx={sxStylesMobile.checkView}>
                      <CheckCircleSvg width={20} />
                    </View>
                    <TextCaption semiBold sx={sxStylesMobile.textItem}>
                      {t('exchange-account::landing-page.text--margin-leverage')}
                    </TextCaption>
                  </View>
                  <View sx={sxStylesMobile.listTextItem}>
                    <View sx={sxStylesMobile.checkView}>
                      <CheckCircleSvg width={20} />
                    </View>
                    <TextCaption semiBold sx={sxStylesMobile.textItem}>
                      {t('exchange-account::landing-page.text--view-and-manage')}
                    </TextCaption>
                  </View>
                  <View sx={sxStylesMobile.confirmTextView}>
                    <UniCheckbox
                      sx={sxStylesMobile.checkBox}
                      value={selected}
                      onValueChange={(value) => onSelectChange(value)}
                      verticalAlignCenter></UniCheckbox>
                    <View sx={sxStylesMobile.confirmTextDetail}>
                      <TextSmall sx={sxStylesMobile.confirmText}>
                        {t('exchange-account::landing-page.text--application-notice', { vendor: store.vendor ?? 'UTGL' })}
                      </TextSmall>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
          <View sx={{ ...sxStylesMobile.btnView, ...{ mb: insets.bottom + 25 } }}>
            <View sx={sxStylesMobile.btnOpenAccountView}>
              <UniButton
                disabled={!selected}
                label={t('exchange-account::landing-page.button--get-started')}
                onPress={() => onBtnOpenAccountClick()}
                sx={sxStylesMobile.buttonOpenAccount}
                size="medium"
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStylesMobile: Record<string, SxProp> = {
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    px: '$4',
    py: '$3'
  },
  textHeader: {
    color: '$textDarkBackground'
  },
  imageView: {
    mt: '$5'
  },
  checkView: {
    width: 20,
    marginTop: -5
  },
  introTextView: {
    mb: '$5',
    alignItems: 'center'
  },
  introText: {
    color: '$textDarkBackground',
    textAlign: 'center'
  },
  mainView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  infoView: {
    height: '58%',
    width: '92%',
    alignSelf: 'center',
    flexDirection: 'column',
    pb: '$10'
  },
  graphicView: {
    mt: '$5',
    height: '30%',
    alignItems: 'center',
    flexDirection: 'column'
  },
  columnView: {
    flexDirection: 'column',
    alignItems: 'center'
  },
  buttonOpenAccount: {
    height: 44
  },
  listTextItem: {
    mb: '$3',
    flexDirection: 'row',
    alignSelf: 'start'
  },
  titleView: {
    textAlign: 'center',
    mb: '$3',
    mt: '$1'
  },
  whyTextItem: {
    color: '$textDarkBackground'
  },
  textItem: {
    pl: '$1',
    color: '$textDarkBackground',
    width: '92%'
  },
  confirmTextView: {
    pt: '$3',
    flexDirection: 'row',
    alignSelf: 'start'
  },
  confirmText: {
    color: '$textDarkBackground'
  },
  checkBox: {
    alignSelf: 'flex-start',
    mr: '$1'
  },
  titleText: {
    color: '#E3B354'
  },
  btnView: {
    flexDirection: 'column',
    height: '7%',
    alignItems: 'center'
  },
  btnOpenAccountView: {
    width: '90%',
    pt: '$10',
    mt: -5
  },
  buttonHeight: {
    height: 44
  },
  listTextView: {
    alignSelf: 'start'
  },
  confirmTextDetail: {
    width: '92%'
  }
};
